
import { useRef, useEffect, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent } from "@/components/ui/dialog";

interface Message {
  id: string | number;
  senderId: string;
  text: string;
  timestamp: string;
  isRead: boolean;
  attachment?: string;
  attachmentName?: string;
}

interface MessagesListProps {
  messages: Message[];
  currentUserId: string;
  otherUserName: string;
  otherUserAvatar: string;
  isLoading: boolean;
  isTyping: boolean;
  typingUsers: string[];
}

const MessagesList = ({ 
  messages, 
  currentUserId, 
  otherUserName, 
  otherUserAvatar, 
  isLoading,
  isTyping,
  typingUsers
}: MessagesListProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousConversationRef = useRef<string>('');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const scrollToBottom = (smooth = false) => {
    const container = containerRef.current?.parentElement;
    if (container) {
      if (smooth) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      } else {
        container.scrollTop = container.scrollHeight;
      }
    }
  };

  useEffect(() => {
    const currentConversation = otherUserName;
    const isNewConversation = currentConversation !== previousConversationRef.current;
    
    // Always scroll to bottom for new conversations or when messages first load
    if (isNewConversation && currentConversation) {
      console.log("New conversation detected, scrolling to bottom:", currentConversation);
      previousConversationRef.current = currentConversation;
      
      // Use timeout to ensure DOM has rendered
      setTimeout(() => {
        scrollToBottom(false);
      }, 100);
      return;
    }

    // For the same conversation, check if user is near bottom before auto-scrolling
    if (!isNewConversation && messages.length > 0) {
      const container = containerRef.current?.parentElement;
      if (container) {
        const isNearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 100;
        if (isNearBottom) {
          scrollToBottom(true);
        }
      }
    }
  }, [messages, otherUserName]);

  // Initial scroll to bottom when messages first load
  useEffect(() => {
    if (messages.length > 0 && !isLoading) {
      setTimeout(() => {
        scrollToBottom(false);
      }, 50);
    }
  }, [messages.length, isLoading]);

  const getInitials = (name: string) => {
    if (!name) return "?";
    return name
      .split(' ')
      .filter(word => word.length > 0)
      .map(word => word[0])
      .join('')
      .toUpperCase();
  };

  const isImageAttachment = (url: string) => {
    return url.match(/\.(jpeg|jpg|gif|png|webp)$/i);
  };

  const openImageModal = (imageUrl: string) => {
    setSelectedImage(imageUrl);
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'xls':
      case 'xlsx':
        return '📊';
      case 'zip':
      case 'rar':
        return '🗜️';
      case 'mp3':
      case 'wav':
        return '🎵';
      case 'mp4':
      case 'avi':
        return '🎬';
      default:
        return '📎';
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4" ref={containerRef}>
        {[1, 2, 3].map((i) => (
          <div key={i} className={`flex ${i % 2 === 0 ? "justify-end" : "justify-start"}`}>
            {i % 2 !== 0 && <Skeleton className="h-8 w-8 rounded-full mr-2" />}
            <Skeleton className={`h-16 w-64 rounded-lg`} />
          </div>
        ))}
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="h-full flex items-center justify-center" ref={containerRef}>
        <div className="text-center text-gray-500">
          <p className="mb-2">No messages yet</p>
          <p className="text-sm">Send a message to start the conversation</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4" ref={containerRef}>
        {messages.map((message) => (
          <div 
            key={message.id} 
            className={`flex ${message.senderId === currentUserId ? "justify-end" : "justify-start"}`}
          >
            {message.senderId !== currentUserId && (
              <Avatar className="h-8 w-8 mr-2 mt-1">
                <AvatarImage src={otherUserAvatar} alt={otherUserName} />
                <AvatarFallback>{getInitials(otherUserName)}</AvatarFallback>
              </Avatar>
            )}
            <div 
              className={`max-w-[75%] px-4 py-3 rounded-lg ${
                message.senderId === currentUserId 
                  ? "bg-emu-blue text-white rounded-tr-none" 
                  : "bg-white border rounded-tl-none"
              }`}
            >
              {message.text && (
                <p className="text-sm mb-2">{message.text}</p>
              )}

              {message.attachment && (
                <div className="mt-2">
                  {isImageAttachment(message.attachment) ? (
                    <div 
                      className="cursor-pointer rounded overflow-hidden border hover:opacity-90 transition-opacity"
                      onClick={() => openImageModal(message.attachment!)}
                    >
                      <img 
                        src={message.attachment} 
                        alt={message.attachmentName || "Image"} 
                        className="max-w-full max-h-60 object-cover"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <a 
                      href={message.attachment} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block rounded overflow-hidden border bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                      <div className="p-3 text-xs font-medium flex items-center space-x-2">
                        <span>{getFileIcon(message.attachmentName || "")}</span>
                        <span>{message.attachmentName || "Attachment"}</span>
                        <span className="text-gray-500">↗</span>
                      </div>
                    </a>
                  )}
                </div>
              )}

              <div 
                className={`text-xs mt-2 ${
                  message.senderId === currentUserId ? "text-blue-100" : "text-gray-500"
                }`}
              >
                {message.timestamp}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
        {isTyping && typingUsers.length > 0 && (
          <div className="flex items-center gap-2 pl-12 mt-3">
            <div className="flex gap-1">
              <div className="w-2 h-2 rounded-full bg-emu-blue animate-bounce [animation-delay:0s]" />
              <div className="w-2 h-2 rounded-full bg-emu-blue animate-bounce [animation-delay:0.15s]" />
              <div className="w-2 h-2 rounded-full bg-emu-blue animate-bounce [animation-delay:0.3s]" />
            </div>
            <span className="text-xs text-emu-blue ml-2">
              {typingUsers[0]} is typing...
            </span>
          </div>
        )}
      </div>

      {/* Image Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-2">
          {selectedImage && (
            <img 
              src={selectedImage} 
              alt="Full size image" 
              className="w-full h-full object-contain rounded"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MessagesList;
